import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Upload, ClipboardPaste, Check, X } from "lucide-react";

const ROWS = [
  ["read", "Lignes lues", "text-white"], ["invalid", "Format invalide", "text-rose-300"],
  ["dup_in_file", "Doublons dans le fichier", "text-amber-300"], ["dup_in_campaign", "Déjà présents", "text-amber-300"],
  ["suppressed", "Désinscrits (exclus)", "text-rose-300"], ["disposable", "Adresses jetables", "text-rose-300"],
  ["unverified", "Domaine inexistant", "text-rose-300"], ["in_other_campaigns", "Déjà dans une campagne", "text-sky-300"],
];

// Import CSV / Excel (.xlsx) / ZIP ou collage, avec analyse des doublons avant confirmation.
export const ImportPanel = ({ cid, onDone }) => {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [an, setAn] = useState(null);
  const [excludeOther, setExcludeOther] = useState(false);
  const [step, setStep] = useState("");
  const [view, setView] = useState("ok");

  // L'analyse tourne en arrière-plan sur le serveur : on suit son avancement.
  const follow = async (job) => {
    let fails = 0;
    for (;;) {
      await new Promise((r) => setTimeout(r, 2000));
      let r;
      try { r = await api.get(`/admin/email-campaigns/${cid}/import/${job}/status`); fails = 0; }
      catch (e) { if (++fails > 20) throw e; continue; }
      setStep(r.data.step || "");
      if (r.data.status === "done") return r.data.analysis;
      if (r.data.status === "error") throw new Error(r.data.error || "Analyse impossible");
    }
  };

  const analyzeFile = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    setStep("Envoi du fichier…");
    try { const r = await api.post(`/admin/email-campaigns/${cid}/import/analyze`, fd); setAn(await follow(r.data.job_id)); }
    catch (e) { toast.error(e.response?.data?.detail || e.message || "Analyse impossible"); } finally { setBusy(false); setStep(""); }
  };
  const analyzeText = async () => {
    if (!text.trim()) return toast.info("Collez des adresses e-mail.");
    setBusy(true);
    try { const r = await api.post(`/admin/email-campaigns/${cid}/import/analyze-text`, { text }); setAn(await follow(r.data.job_id)); }
    catch (e) { toast.error(e.response?.data?.detail || e.message || "Analyse impossible"); } finally { setBusy(false); setStep(""); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/email-campaigns/${cid}/import/${an.job_id}/confirm`, { exclude_other_campaigns: excludeOther });
      toast.success(`${r.data.added} contact(s) importé(s)`); setAn(null); setText(""); onDone?.();
    } catch { toast.error("Import impossible"); } finally { setBusy(false); }
  };
  const cancel = async () => { try { await api.delete(`/admin/email-campaigns/${cid}/import/${an.job_id}`); } catch { /* */ } setAn(null); };
  const onDrop = (e) => { e.preventDefault(); const f = e.dataTransfer?.files?.[0]; if (f) analyzeFile(f); else setText((p) => p + (e.dataTransfer?.getData("text") || "")); };

  if (an) {
    const willImport = excludeOther && cid !== "base" ? an.to_import - an.in_other_campaigns : an.to_import;
    return (
      <div className="rounded-xl border border-sky-400/30 bg-sky-500/[0.05] p-3" data-testid={`import-analysis-${cid}`}>
        <p className="mb-2 text-sm font-semibold text-white">Analyse {an.filename ? `de « ${an.filename} »` : "des adresses collées"}</p>
        <div className="grid grid-cols-2 gap-1.5 text-xs sm:grid-cols-4">
          {ROWS.map(([k, l, c]) => (
            <div key={k} className="rounded-lg bg-black/30 px-2 py-1.5"><span className={`font-bold ${c}`} data-testid={`analysis-${k}`}>{an[k]}</span> <span className="text-muted-foreground">{l}</span></div>
          ))}
        </div>
        <div className="mt-3 flex gap-1.5">
          <button onClick={() => setView("ok")} className={`rounded-full border px-2.5 py-1 text-[11px] ${view === "ok" ? "border-emerald-400 bg-emerald-500 text-white" : "border-white/15 text-muted-foreground"}`} data-testid="analysis-view-ok">Adresses retenues ({an.to_import})</button>
          <button onClick={() => setView("ko")} className={`rounded-full border px-2.5 py-1 text-[11px] ${view === "ko" ? "border-rose-400 bg-rose-500 text-white" : "border-white/15 text-muted-foreground"}`} data-testid="analysis-view-ko">Adresses écartées ({an.read - an.to_import})</button>
        </div>
        <div className="mt-2 max-h-56 overflow-auto rounded-lg border border-white/10 bg-black/20" data-testid="analysis-emails">
          <table className="w-full text-left text-xs">
            <tbody>
              {(view === "ok" ? an.sample || [] : an.rejected_sample || []).map((x, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="px-2 py-1 text-white">{x.email || "(vide)"}</td>
                  <td className="px-2 py-1 text-muted-foreground">{view === "ok" ? [x.first_name, x.last_name].filter(Boolean).join(" ") || x.company || "" : <span className="text-rose-300">{x.reason}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-1 text-[10px] text-muted-foreground">Aperçu des 200 premières adresses · la liste complète des contacts retenus s'affiche dans « Contacts vérifiés » après l'import.</p>
        {cid !== "base" && an.in_other_campaigns > 0 && (
          <label className="mt-2 flex items-center gap-2 text-xs text-sky-200"><input type="checkbox" checked={excludeOther} onChange={(e) => setExcludeOther(e.target.checked)} className="accent-sky-400" data-testid="import-exclude-other" /> Ne pas réimporter les {an.in_other_campaigns} contact(s) déjà présents dans une autre campagne</label>
        )}
        <div className="mt-3 flex items-center justify-between gap-2">
          <p className="text-sm text-emerald-300" data-testid="analysis-to-import"><strong>{willImport}</strong> contact(s) vérifié(s) prêt(s) à importer</p>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" className="rounded-full" onClick={cancel} data-testid="import-cancel"><X size={14} className="mr-1" /> Annuler</Button>
            <Button size="sm" disabled={busy || willImport <= 0} className="rounded-full bg-emerald-500 text-black hover:bg-emerald-400" onClick={confirm} data-testid="import-confirm">{busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />} Importer</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-3" onDrop={onDrop} onDragOver={(e) => e.preventDefault()} data-testid={`import-panel-${cid}`}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept=".csv,.txt,.xlsx,.zip" className="hidden" onChange={(e) => { analyzeFile(e.target.files?.[0]); e.target.value = ""; }} data-testid={`campaign-file-${cid}`} />
        <Button size="sm" variant="outline" disabled={busy} className="rounded-full" onClick={() => fileRef.current?.click()} data-testid={`campaign-import-${cid}`}>
          {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />} Importer CSV / Excel / ZIP
        </Button>
        {busy && step ? <span className="flex items-center gap-1.5 text-xs text-sky-300" data-testid="import-step"><Loader2 size={13} className="animate-spin" /> {step}</span>
          : <span className="text-[11px] text-muted-foreground">ou glissez-déposez un fichier ici · seule la colonne e-mail est obligatoire</span>}
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={"Ou collez des adresses : jean@exemple.fr, marie@exemple.com…"} className="w-full resize-y rounded-lg border border-white/10 bg-black/40 p-2 text-sm text-white outline-none focus:border-sky-400/50" data-testid={`campaign-paste-textarea-${cid}`} />
      <div className="mt-2 flex justify-end">
        <Button size="sm" disabled={busy || !text.trim()} className="rounded-full bg-sky-500 hover:bg-sky-400" onClick={analyzeText} data-testid={`campaign-paste-submit-${cid}`}><ClipboardPaste size={14} className="mr-1.5" /> Analyser ces adresses</Button>
      </div>
    </div>
  );
};
